import React, { useState, useEffect, useContext } from "react";
import { TextInput, Button, Checkbox } from "../../../components";
import { ToastContext } from "../../../contexts/ToastContext/ToastContext";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../../redux/store";
import checqueImg from "../../../assets/images/CHEQUE.png";
import iconsclose from "../../../assets/images/iconsclose.png";
import questionmark from "../../../assets/images/questionmark.png";
import { numberOnly, officeHstNonvalidation, officeHstvalidation, validateAddress } from "../../../utils";
import { Dialog } from "@mui/material";
import { UpdateOfficeBilling } from "../../../models/update-office-billing.model";
import { getOffice, updateOfficeBilling } from "../../../redux/slices/OfficeSlice";
import { formDataInitialState } from "./DepositData";
import { formDataType, FormFieldType } from "./OfficeProfileData";
import { AuthPermission } from "../../../services/auth.service";
import AddressField, { AddressFieldValue, GoogleAddressComponent } from "../../../components/CustomAddress/CustomAddress";
import { useLocation, useNavigate } from "react-router-dom";

const DepositAccount = () => {
  const dispatch: AppDispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const [errorInitiated, setErrorInitiated] = useState(false);
  const [formData, setFormData] = useState(formDataInitialState);
  const toast = useContext(ToastContext);
  const { currentOffice, loading } = useSelector((state: RootState) => state.office);
  const [useCustomAddress, setUseCustomAddress] = useState(false);
  const [hstApplicable, setHstApplicable] = useState(currentOffice?.taxEnabled ? currentOffice?.taxEnabled : false);
  const [open, setOpen] = React.useState(false);
  const [addressValidate, setAddressValidate] = useState(false);
  const [canEdit, setCanEdit] = useState(!currentOffice?.bankCompleted);

  // Check if deposit info exists (any deposit address field is set)
  // If deposit info exists, use ALL deposit fields. If not, fall back to ALL billing fields.
  const hasDepositInfo = !!(currentOffice?.depositStreet || currentOffice?.depositCity || currentOffice?.depositState || currentOffice?.depositZip);

  // Use deposit fields if deposit info exists, otherwise use billing fields entirely
  const depositName = currentOffice?.depositName ?? (hasDepositInfo ? undefined : currentOffice?.billingName);
  const depositStreet = hasDepositInfo ? currentOffice?.depositStreet : currentOffice?.billingStreet;
  const depositStreet1 = hasDepositInfo ? currentOffice?.depositStreet1 : currentOffice?.billingStreet1;
  const depositCity = hasDepositInfo ? currentOffice?.depositCity : currentOffice?.billingCity;
  const depositState = hasDepositInfo ? currentOffice?.depositState : currentOffice?.billingState;
  const depositZip = hasDepositInfo ? currentOffice?.depositZip : currentOffice?.billingZip;
  const depositCountry = hasDepositInfo ? currentOffice?.depositCountry : currentOffice?.billingCountry;

  const billingInfoChecked =
    currentOffice?.name === depositName &&
    `${depositStreet},${depositStreet1} ${depositCity}, ${depositState}, ${depositZip}, ${depositCountry}` ===
      `${currentOffice?.street},${currentOffice?.street1} ${currentOffice?.city}, ${currentOffice?.state}, ${currentOffice?.zip}, ${currentOffice?.country}`;

  const [billingInfo, setBillingInfo] = useState(billingInfoChecked);
  const [billingInfoClicked, setBillingInfoClicked] = useState(false);

  // Map deposit address fields to Google address component format
  // depositStreet = full street address (e.g., "123 Queen St W") -> street_number
  // depositStreet1 = suite/unit (e.g., "Suite 500") -> subpremise
  const initialAddressData = [
    { short_name: `${depositStreet ?? ""}`, long_name: `${depositStreet ?? ""}`, types: ["street_number"] },
    {
      short_name: `${depositCity ?? ""}`,
      long_name: `${depositCity ?? ""}`,
      types: ["sublocality_level_1", "sublocality", "political"],
    },
    { short_name: `${depositCity ?? ""}`, long_name: `${depositCity ?? ""}`, types: ["locality", "political"] },
    {
      short_name: `${depositState ?? ""}`,
      long_name: `${depositState ?? ""}`,
      types: ["administrative_area_level_2", "political"],
    },
    {
      short_name: `${depositState ?? ""}`,
      long_name: `${depositState ?? ""}`,
      types: ["administrative_area_level_1", "political"],
    },
    { short_name: `${depositCountry ?? ""}`, long_name: `${depositCountry ?? ""}`, types: ["country", "political"] },
    { short_name: `${depositZip ?? ""}`, long_name: `${depositZip ?? ""}`, types: ["postal_code"] },
    // Only include subpremise if depositStreet1 has a value
    ...(depositStreet1 ? [{ short_name: depositStreet1, long_name: depositStreet1, types: ["subpremise"] }] : []),
  ];
  const [addressData, setAddressData] = useState({ address_components: initialAddressData || [] });

  const { currentUser } = useSelector((state: RootState) => state.currentUser);
  const permissions = currentUser?.permissions;

  const isAuthorizedToEdit = Array.isArray(permissions) && permissions.includes(AuthPermission.Office);

  useEffect(() => {
    const state = location.state as { openInEditMode?: string } | null;
    if (state?.openInEditMode === "deposit" && isAuthorizedToEdit) {
      setCanEdit(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate, isAuthorizedToEdit]);

  useEffect(() => {
    if (currentOffice !== null) {
      // setCanEdit(!currentOffice?.bankInstitution);
      // Use deposit fields if available, otherwise fallback to billing fields
      const depositName = currentOffice?.depositName;
      const depositStreet = currentOffice?.depositStreet;
      const depositStreet1 = currentOffice?.depositStreet1;
      const depositCity = currentOffice?.depositCity;
      const depositState = currentOffice?.depositState;
      const depositZip = currentOffice?.depositZip;
      const depositCountry = currentOffice?.depositCountry;
      const { taxNumber, bankName, bankInstitution, bankTransit, bankAccount, taxEnabled } = currentOffice;

      const address = depositStreet
        ? [depositStreet, depositStreet1, depositCity, depositState, depositZip, depositCountry]
            .filter(val => typeof val === "string" && val.trim() !== "")
            .join(", ")
        : "";
      const oldFormData = formDataInitialState;
      const updatedFormData = oldFormData.map(data => {
        let value = "";
        let valid = false;
        let required = data.required;
        switch (data.id) {
          case "officeAddress":
            value = address;
            valid = address.trim().length !== 0;
            break;
          case "companyName":
            value = depositName ?? "";
            valid = !!value;
            break;
          case "bankName":
            value = bankName ?? "";
            valid = true;
            break;
          case "hstNumber":
            value = taxNumber ?? "";
            valid = !!value;
            required = !!taxEnabled;
            break;
          case "bankCode":
            value = bankInstitution ?? "";
            valid = !!value;
            break;
          case "transitNumber":
            value = bankTransit ?? "";
            valid = !!value;
            break;
          case "accountNumber": {
            const accVal = (bankAccount ?? "").replace(/\D/g, "");
            value = accVal;
            valid = accVal.length === 7;
            break;
          }
          default:
            value = data.value as string; // Preserve the existing value if no matching case
            valid = data.required || value.length ? !!value : true;
            break;
        }

        return { ...data, value, valid, required };
      });
      setFormData(updatedFormData);
      setUseCustomAddress(!!currentOffice?.customBillingAddress);
    }
  }, [currentOffice]);

  const handleChange = (id: string, value: string | boolean) => {
    const nonRequiredFlds = ["wsibNumber", "hstNumber"];
    const tempFormData = [...formData];

    const setInputValueAndValidity = (input: formDataType, value: string | boolean, isValid: boolean) => {
      input.value = value;
      input.valid = isValid;
    };

    tempFormData.forEach(input => {
      if (input.id === id) {
        switch (id) {
          case "street2":
            setInputValueAndValidity(input, numberOnly(value as string).toString(), !!numberOnly(value as string));
            break;
          case "bankCode":
            const bval = (value as string).replace(/\D/g, "");
            setInputValueAndValidity(input, bval, bval.length > 0 && bval.length === 3);
            break;
          case "hstNumber":
            if (hstApplicable) {
              if ((value as string).length <= 15) {
                setInputValueAndValidity(input, value, officeHstNonvalidation(value as string));
              } else if ((value as string).length > 15 && (value as string).length <= 17 && (value as string).includes(" ")) {
                setInputValueAndValidity(input, value, officeHstvalidation(value as string));
              }
            } else {
              setInputValueAndValidity(input, "", true);
            }
            break;
          case "accountNumber":
            const Depval = (value as string).replace(/\D/g, "");
            setInputValueAndValidity(input, Depval, Depval.length > 0 && Depval.length === 7);
            break;
          case "transitNumber":
            const tval = (value as string).replace(/\D/g, "");
            setInputValueAndValidity(input, tval, tval.length > 0 && tval.length === 5);
            break;
          default:
            input.value = value;
            if (!nonRequiredFlds.includes(id)) {
              input.valid = typeof value === "string" ? value.length > 0 : value === true;
            }
            break;
        }
      }
    });

    setFormData(tempFormData);
  };

  useEffect(() => {
    if (billingInfoClicked) {
      if (billingInfo === true && currentOffice) {
        handleChange(
          "officeAddress",
          [currentOffice?.street, currentOffice?.street1, currentOffice?.city, currentOffice?.state, currentOffice?.zip, currentOffice?.country]
            .filter(Boolean)
            .join(", ")
        );
        handleChange("companyName", currentOffice.name);
        setErrorInitiated(false);
        setAddressValidate(false);
      } else {
        handleChange("officeAddress", "");
        handleChange("companyName", "");
      }
    }
  }, [billingInfo, billingInfoClicked]);

  const validateForm = () => {
    return formData.every(val => (val.required ? val.valid : true));
  };

  const handlehstApplicable = (e: boolean) => {
    setHstApplicable(e);
    let tempFormData = [...formData];
    const index = tempFormData.findIndex(item => item.id === "hstNumber");
    const item = tempFormData.find(item => item.id === "hstNumber");
    if (index !== -1 && item) {
      tempFormData = [...tempFormData.slice(0, index), { ...item, valid: !e, required: e, value: "" }, ...tempFormData.slice(index + 1)];
    }
    setFormData(tempFormData);
    setUseCustomAddress(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const getAddOfficeData = () => {
    return ["companyName", "hstNumber", "accountNumber", "bankCode", "transitNumber", "bankName"]
      .map(key => {
        const foundVal = formData?.find(el => el.id === key)?.value as string;
        return {
          [key]: foundVal ? foundVal : "",
        };
      })
      .reduce((acc, el) => {
        return { ...acc, ...el };
      }, {});
  };

  const updateOfficeBillingData = (dataToSend: UpdateOfficeBilling) => {
    if (currentOffice) {
      dispatch(updateOfficeBilling(dataToSend)).then(async act => {
        if (act.payload !== null && currentOffice.officeId) {
          await dispatch(getOffice({ officeId: currentOffice.officeId }));
          toast?.openToast("Updated Successfully!");
          setCanEdit(false);
        }
      });
    }
  };

  const saveBillingInfo = () => {
    //toast?.openToast();
    if (currentOffice?.officeId) {
      const { companyName, hstNumber, accountNumber, bankCode, transitNumber, bankName } = getAddOfficeData();
      if (!billingInfo) {
        const addressValidation = validateAddress(addressData.address_components);
        if (addressValidation === null) {
          setAddressValidate(true);
          return;
        }
        // Match OfficeProfileTab logic:
        // - depositStreet = streetNumber + street (e.g., "123 Queen St W")
        // - depositStreet1 = unit/suite (e.g., "Unit 400")
        // - depositStreet2 = "" (always empty)
        const depositStreet = [addressValidation.streetNumber, addressValidation.street].filter(Boolean).join(" ").trim();
        const depositStreet1 = addressValidation.unit ?? "";
        const dataToSend: UpdateOfficeBilling = {
          officeId: currentOffice?.officeId,
          // Send deposit fields instead of billing fields
          depositName: companyName,
          depositStreet: depositStreet,
          depositStreet1: depositStreet1,
          depositStreet2: "",
          customBillingAddress: useCustomAddress,
          depositCity: addressValidation.city,
          depositState: addressValidation.state,
          depositZip: addressValidation.zip,
          depositCountry: addressValidation.country,
          taxEnabled: hstApplicable,
          taxNumber: hstNumber.length ? hstNumber : undefined,
          bankAccount: accountNumber,
          bankName,
          bankInstitution: bankCode,
          bankTransit: transitNumber,
        };
        setErrorInitiated(false);
        updateOfficeBillingData(dataToSend);
      } else {
        const dataToSend: UpdateOfficeBilling = {
          officeId: currentOffice?.officeId,
          // Send deposit fields instead of billing fields
          depositName: currentOffice?.name,
          depositStreet: currentOffice?.street,
          depositStreet1: currentOffice?.street1,
          depositStreet2: "",
          customBillingAddress: useCustomAddress,
          depositCity: currentOffice?.city,
          depositState: currentOffice?.state,
          depositZip: currentOffice?.zip,
          depositCountry: currentOffice?.country,
          taxEnabled: hstApplicable,
          taxNumber: hstNumber.length ? hstNumber : undefined,
          bankAccount: accountNumber,
          bankName,
          bankInstitution: bankCode,
          bankTransit: transitNumber,
        };
        setErrorInitiated(false);
        updateOfficeBillingData(dataToSend);
      }
    }
  };

  const renderInput = (inp: formDataType, isReadOnly: boolean) => {
    switch (inp.type) {
      case FormFieldType.TextInput:
        const fullWidthInputs = ["companyName", "officeAddress", "bankName", "bankCode"];
        const isFullWidth = fullWidthInputs.includes(inp.id);
        const colClass = isFullWidth && !isReadOnly ? "col-span-2" : "col-span-1 md:col-span-1";
        return (
          <div className={colClass}>
            {isReadOnly ? (
              <div className="w-full">
                <label className="text-sm text-docsigna-blue-dark font-medium block w-full max-w-full mb-1">{inp.title}</label>
                <h2 className="text-lg font-medium text-docsigna-purple">{inp.value}</h2>
              </div>
            ) : (
              <>
                <TextInput
                  key={inp.id}
                  value={inp.value as string}
                  onChangeFunc={e => handleChange(inp.id, e.currentTarget.value)}
                  placeholder={inp.placeholder}
                  errorMsg={inp.errorMessage}
                  isError={!inp.valid && errorInitiated}
                  label={inp.title}
                  isRequired={inp.required}
                  disabled={inp.id === "companyName" && billingInfo}
                  onBlur={e => handleChange(inp.id, e.currentTarget.value.trim())}
                />
              </>
            )}
          </div>
        );

      case FormFieldType.WSIB:
        return (
          <div className="col-span-2 md:col-span-1" key={inp.id}>
            {isReadOnly ? (
              <div className="w-full">
                <label className="text-sm text-docsigna-blue-dark font-medium block w-full max-w-full mb-1">{inp.title}</label>
                <h2 className="text-lg font-medium text-docsigna-purple">{inp.value ?? "-"}</h2>
              </div>
            ) : (
              <TextInput
                key={inp.id}
                value={inp.value as string}
                onChangeFunc={e => handleChange(inp.id, e.currentTarget.value)}
                placeholder={inp.placeholder}
                errorMsg={inp.errorMessage}
                isError={!inp.valid && errorInitiated}
                label={inp.title}
                isRequired={inp.required}
                onBlur={e => handleChange(inp.id, e.currentTarget.value.trim())}
              />
            )}
          </div>
        );
      case FormFieldType.HST:
        return (
          <div className="col-span-2 md:col-span-1">
            {isReadOnly ? (
              hstApplicable ? (
                <div className="w-full">
                  <label className="text-sm text-docsigna-blue-dark font-medium block w-full max-w-full mb-1">{inp.title}</label>
                  <h2 className="text-lg font-medium text-docsigna-purple">{inp.value}</h2>
                </div>
              ) : null
            ) : (
              <div className="grid grid-cols-1 gap-4">
                <Checkbox
                  label={"HST applicable on services and fees for this office"}
                  checked={hstApplicable}
                  onChange={e => {
                    handlehstApplicable(e.currentTarget.checked);
                  }}
                />
                {hstApplicable ? (
                  <TextInput
                    key={inp.id}
                    value={inp.value as string}
                    onChangeFunc={e => handleChange(inp.id, e.currentTarget.value)}
                    placeholder={inp.placeholder}
                    errorMsg={inp.errorMessage}
                    isError={!inp.valid && errorInitiated}
                    label={inp.title}
                    isRequired={hstApplicable}
                    onBlur={e => handleChange(inp.id, e.currentTarget.value.trim())}
                  />
                ) : null}
              </div>
            )}
          </div>
        );

      case FormFieldType.Places: {
        // Update: control column span based on edit mode
        const spanClass = isReadOnly ? "col-span-1 md:col-span-1" : "col-span-2 md:col-span-2";

        if (!billingInfo) {
          return isReadOnly ? (
            <div className={spanClass} key={inp.id}>
              <label className="text-sm text-docsigna-blue-dark font-medium block mb-1">{inp.title}</label>
              <div className="text-lg font-medium text-docsigna-purple">
                {depositStreet && <div>{depositStreet}</div>}
                {depositStreet1 && <div>{depositStreet1}</div>}
                <div>{[depositCity, depositState, depositZip].filter(Boolean).join(", ")}</div>
              </div>
            </div>
          ) : (
            <div className={spanClass} key={inp.id}>
              <AddressField
                label={inp.title}
                required={inp.required}
                hideGooglePlaceSearch={!!currentOffice?.customBillingAddress}
                initialValue={inp.value as string}
                initialComponents={addressData.address_components as GoogleAddressComponent[]}
                disabled={!canEdit}
                error={(!inp.valid && errorInitiated) || addressValidate}
                errorMsg={inp.errorMessage}
                onModeChange={isManual => setUseCustomAddress(isManual)}
                onChange={newVal => {
                  const tmp = [...formData];
                  const idx = tmp.findIndex(f => f.id === "officeAddress");
                  if (idx >= 0) {
                    tmp[idx] = { ...tmp[idx], value: newVal.value, valid: newVal.isValid };
                    setFormData(tmp);
                  }
                  setAddressData({ address_components: newVal.components });
                }}
                initialData={currentOffice}
                addressType="billing"
                darkBg={false}
              />
            </div>
          );
        } else {
          // billingInfo === true: using office address
          return (
            <div className={spanClass} key={inp.id}>
              {isReadOnly ? (
                <div className="w-full">
                  <label className="text-sm text-docsigna-blue-dark font-medium block mb-1">{inp.title}</label>
                  <div className="text-lg font-medium text-docsigna-purple">
                    {currentOffice?.street && <div>{currentOffice.street}</div>}
                    {currentOffice?.street1 && <div>{currentOffice.street1}</div>}
                    <div>{[currentOffice?.city, currentOffice?.state, currentOffice?.zip].filter(Boolean).join(", ")}</div>
                  </div>
                </div>
              ) : (
                <TextInput
                  key={inp.id}
                  value={inp.value as string}
                  onChangeFunc={e => handleChange(inp.id, e.currentTarget.value)}
                  placeholder={inp.placeholder}
                  errorMsg={inp.errorMessage}
                  isError={!inp.valid && errorInitiated}
                  label={inp.title}
                  isRequired={inp.required}
                  disabled={billingInfo}
                  onBlur={e => handleChange(inp.id, e.currentTarget.value.trim())}
                />
              )}
            </div>
          );
        }
      }

      case FormFieldType.Number:
        return (
          <div className="col-span-2 md:col-span-2">
            {isReadOnly ? (
              <div className="w-full">
                <label className="text-sm text-docsigna-blue-dark font-medium block mb-1">{inp.title}</label>
                <h2 className="text-lg font-medium text-docsigna-purple">{inp.value}</h2>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 w-full">
                  <div className="col-span-1">
                    <div className="flex items-center w-full mb-1">
                      <label className="text-sm text-gray-700">
                        {inp.title}
                        {inp.required && <span className="text-red-500">*</span>}
                      </label>
                      {inp.id !== "wsibNumber" && (
                        <span
                          onClick={() => setOpen(true)}
                          className="ml-auto w-4 h-4 bg-docsigna-orange flex items-center justify-center rounded-full cursor-pointer"
                        >
                          <img src={questionmark} className="h-3" alt="help" />
                        </span>
                      )}
                    </div>

                    <TextInput
                      key={inp.id}
                      value={inp.value as string}
                      onChangeFunc={e => handleChange(inp.id, e.currentTarget.value)}
                      placeholder={inp.placeholder}
                      errorMsg={inp.errorMessage}
                      isError={!inp.valid && errorInitiated}
                      onBlur={e => handleChange(inp.id, e.currentTarget.value.trim())}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="relative md:flex gap-5">
      <div>
        <div className="flex border-b border-docsigna-purple border-opacity-40 py-1 my-2 col-span-2">
          <h2 className="uppercase text-lg font-bold">Deposit Information</h2>
        </div>
        <div className="m-2">
          {canEdit ? (
            <Checkbox
              id="billing-info"
              checked={billingInfo}
              onChange={e => {
                setBillingInfo(e.currentTarget.checked);
                setBillingInfoClicked(true);
              }}
              label="Deposit Account contact info is the same as office profile"
            />
          ) : null}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 w-full">
          {formData.map(input => {
            return (
              <>
                {renderInput(input, !canEdit)}
                {input.id !== "officeAddress" ? <div className="col-span-2 md:col-span-1 opacity-0 pointer-events-none h-0"></div> : null}
              </>
            );
          })}
        </div>
      </div>
      <div className="py-2 col">
        {isAuthorizedToEdit ? (
          canEdit ? (
            <Button
              className="profile-btn"
              width="fit"
              onClick={() => {
                setErrorInitiated(true);
                if (validateForm()) saveBillingInfo();
              }}
              disabled={(!validateForm() && errorInitiated) || loading}
              variant={loading ? "loading" : "Primary"}
              text="Save"
            />
          ) : (
            <Button
              className="profile-btn"
              width="fit"
              onClick={() => {
                setCanEdit(true);
              }}
              text="Edit"
            />
          )
        ) : null}
      </div>
      <Dialog maxWidth={"md"} open={open} onClose={handleClose}>
        <div className="relative fit-content">
          {" "}
          <img src={checqueImg} alt="" />
          <span className="modal-close absolute top-2 right-1 w-7 h-7" onClick={handleClose}>
            <img src={iconsclose} alt="" className="w-5 h-5 cursor-pointer" />
          </span>
        </div>
      </Dialog>
    </div>
  );
};

export default DepositAccount;
